import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoading } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { ProgressBar } from "@/components/common/Progress";
import { RagBadge } from "@/components/common/RagBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { fmtDate, PLAN_STATUS, relDays } from "@/lib/format";
import { toast } from "sonner";
import dayjs from "dayjs";

export default function Plan() {
  const { orgId } = useOrg();
  const { canEdit } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftOpen, setDraftOpen] = useState(false);
  const [draftStep, setDraftStep] = useState("input");
  const [drafting, setDrafting] = useState(false);
  const [draft, setDraft] = useState(null);
  const [creatingDraft, setCreatingDraft] = useState(false);
  const [notes, setNotes] = useState("");
  const [attached, setAttached] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    title: "", start_date: dayjs().format("YYYY-MM-DD"),
    end_date: dayjs().add(90, "day").format("YYYY-MM-DD"),
    objective: "", context: "", success_definition: "",
  });

  const load = () => api.get(`/plans?org_id=${orgId}`).then((r) => setPlans(r.data));
  useEffect(() => { if (orgId) { setPlans(null); load(); } }, [orgId]);

  const create = async () => {
    if (!form.title) return toast.error("Please add a plan title");
    setSaving(true);
    try {
      const { data } = await api.post(`/plans?org_id=${orgId}`, form);
      toast.success("Plan created");
      setOpen(false);
      navigate(`/plan/${data.id}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/plans/${id}/duplicate`);
      toast.success("Plan duplicated as draft");
      navigate(`/plan/${data.id}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const startDraft = () => {
    setDraftOpen(true); setDraftStep("input"); setDraft(null); setNotes(""); setAttached([]);
  };

  const generate = async () => {
    setDrafting(true); setDraft(null); setDraftStep("review");
    try {
      const { data } = await api.post(`/ai/draft-plan?org_id=${orgId}`, { notes });
      const jobId = data.job_id;
      const startedAt = Date.now();
      // Poll the background job so no single request waits on the ~30s AI call (avoids gateway timeouts)
      while (true) {
        await new Promise((r) => setTimeout(r, 2500));
        const { data: s } = await api.get(`/ai/draft-plan/${jobId}`);
        if (s.status === "done") { setDraft(s.result); break; }
        if (s.status === "error") throw new Error(s.error || "Amden couldn't draft the plan. Please try again.");
        if (Date.now() - startedAt > 150000) throw new Error("This is taking longer than usual. Please try again in a moment.");
      }
    } catch (e) {
      const msg = e.response?.data?.detail ? apiError(e.response.data.detail) : (e.message || "Couldn't draft the plan. Please try again.");
      toast.error(msg);
      setDraftStep("input");
    } finally { setDrafting(false); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/ai/extract-document?org_id=${orgId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setNotes((n) => (n ? n + "\n\n" : "") + data.text);
      setAttached((a) => [...a, { name: data.filename, chars: data.chars }]);
      toast.success(`Added ${data.chars.toLocaleString()} characters from ${data.filename}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const createFromDraft = async () => {
    if (!draft) return;
    setCreatingDraft(true);
    try {
      const payload = {
        title: draft.title,
        start_date: dayjs().format("YYYY-MM-DD"),
        end_date: dayjs().add(90, "day").format("YYYY-MM-DD"),
        objective: draft.objective,
        success_definition: draft.success_definition,
        status: "active",
      };
      const { data: plan } = await api.post(`/plans?org_id=${orgId}`, payload);
      for (const p of draft.priorities) {
        await api.post(`/priorities?org_id=${orgId}`, {
          plan_id: plan.id, title: p.title, business_area: p.business_area,
          why_it_matters: p.why_it_matters, desired_outcome: p.desired_outcome, success_measure: p.success_measure,
        });
      }
      toast.success("Amden drafted your plan");
      setDraftOpen(false);
      navigate(`/plan/${plan.id}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setCreatingDraft(false); }
  };

  if (!plans) return <PageLoading />;

  return (
    <div data-testid="plan-page">
      <PageHeader
        eyebrow="Strategic focus"
        title="My Plan"
        subtitle="Turn strategy into three to five priorities you can actually deliver each quarter."
        icon="Target"
        actions={canEdit && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={startDraft} disabled={drafting} className="border-[#E4E8EF] hover:border-[#FFC24D]" data-testid="draft-plan-btn">
              <Icons.Sparkles size={16} className="mr-1.5 text-[#FFB000]" /> Draft with Amden
            </Button>
            <Button onClick={() => setOpen(true)} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="new-plan-btn">
              <Icons.Plus size={16} className="mr-1.5" /> New plan
            </Button>
          </div>
        )}
      />

      {plans.length === 0 ? (
        <EmptyState
          icon="Target"
          title="No plans yet"
          description="Create your first plan to focus the period ahead."
          action={canEdit && <Button onClick={() => setOpen(true)} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]">Create plan</Button>}
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {plans.map((p) => (
            <div
              key={p.id}
              onClick={() => navigate(`/plan/${p.id}`)}
              className="amden-card amden-card-hover cursor-pointer p-6"
              data-testid={`plan-card-${p.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <RagBadge rag={PLAN_STATUS[p.status]?.rag}>{PLAN_STATUS[p.status]?.label}</RagBadge>
                  <h3 className="mt-2 font-display text-lg font-bold text-[#0B1729]">{p.title}</h3>
                  <p className="mt-1 text-sm text-[#1E2D44]/60 line-clamp-2">{p.objective}</p>
                </div>
                {canEdit && (
                  <button
                    onClick={(e) => { e.stopPropagation(); duplicate(p.id); }}
                    className="rounded-lg p-2 text-[#1E2D44]/40 hover:bg-[#F4F6F9] hover:text-[#FFB000]"
                    title="Duplicate into new cycle"
                    data-testid={`duplicate-plan-${p.id}`}
                  >
                    <Icons.Copy size={16} />
                  </button>
                )}
              </div>
              <div className="mt-4">
                <div className="mb-1.5 flex items-center justify-between text-xs">
                  <span className="text-[#1E2D44]/55">{p.priority_count} priorities</span>
                  <span className="font-semibold text-[#0B1729]">{p.percent}%</span>
                </div>
                <ProgressBar value={p.percent} />
              </div>
              <div className="mt-4 flex items-center gap-4 border-t border-[#EEF1F6] pt-3 text-xs text-[#1E2D44]/55">
                <span className="flex items-center gap-1"><Icons.Calendar size={13} /> {fmtDate(p.start_date)} – {fmtDate(p.end_date)}</span>
                {relDays(p.end_date) >= 0 && <span className="flex items-center gap-1"><Icons.Clock size={13} /> {relDays(p.end_date)} days left</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New plan</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Plan title</Label><Input data-testid="plan-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Q3 Growth & Accountability Plan" className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1.5" /></div>
              <div><Label>End date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div><Label>Overall objective</Label><Textarea value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} placeholder="What must this cycle achieve?" className="mt-1.5" /></div>
            <div><Label>Definition of success</Label><Textarea value={form.success_definition} onChange={(e) => setForm({ ...form, success_definition: e.target.value })} placeholder="What does success look like?" className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={saving} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="save-plan-btn">
              {saving ? <Icons.Loader2 className="animate-spin" size={16} /> : "Create plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={draftOpen} onOpenChange={setDraftOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="draft-plan-dialog">
          <DialogHeader>
            <div className="flex items-center gap-2 text-[#FFB000]"><Icons.Sparkles size={16} /><span className="text-xs font-semibold uppercase tracking-wide">Draft with Amden</span></div>
            <DialogTitle className="font-display text-xl">
              {draftStep === "input" ? "Give Amden something to work from" : "Your first-draft plan"}
            </DialogTitle>
          </DialogHeader>

          {draftStep === "input" ? (
            <div className="space-y-4">
              <p className="text-sm text-[#1E2D44]/60">
                Amden always uses the company dossier and latest health check. You can also paste extra notes or upload a document (a brief, strategy paper, meeting notes) and Amden will build the plan around it.
              </p>
              <div>
                <Label>Notes, brief or context <span className="font-normal text-[#1E2D44]/45">(optional)</span></Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Paste any strategy notes, goals, meeting minutes or priorities you'd like the plan to reflect…"
                  rows={7}
                  className="mt-1.5"
                  data-testid="draft-notes-input"
                />
              </div>
              <div>
                <input ref={fileRef} type="file" accept=".pdf,.docx" onChange={onUpload} className="hidden" data-testid="draft-file-input" />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="border-dashed border-[#C9D2DE] hover:border-[#FFC24D]"
                  data-testid="draft-upload-btn"
                >
                  {uploading ? <Icons.Loader2 className="mr-1.5 animate-spin" size={16} /> : <Icons.Upload size={16} className="mr-1.5" />}
                  {uploading ? "Reading document…" : "Upload PDF or Word document"}
                </Button>
                {attached.length > 0 && (
                  <div className="mt-2.5 space-y-1.5" data-testid="draft-attached-list">
                    {attached.map((a, i) => (
                      <div key={i} className="flex items-center gap-2 rounded-lg bg-[#F4F6F9] px-3 py-2 text-xs text-[#1E2D44]/70">
                        <Icons.FileText size={14} className="text-[#B87F1E]" />
                        <span className="truncate font-medium text-[#0B1729]">{a.name}</span>
                        <span className="ml-auto shrink-0 text-[#1E2D44]/45">{a.chars.toLocaleString()} chars</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : drafting || !draft ? (
            <div className="flex flex-col items-center justify-center py-14 text-center">
              <Icons.Loader2 className="animate-spin text-[#FFB000]" size={30} />
              <p className="mt-3 text-sm text-[#1E2D44]/60">Amden is reviewing the dossier, health score{attached.length ? ", your uploaded document" : ""} and current situation…</p>
            </div>
          ) : (
            <div className="space-y-5">
              <div>
                <Label>Plan title</Label>
                <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="mt-1.5" data-testid="draft-title-input" />
              </div>
              <div>
                <Label>Overall objective</Label>
                <Textarea value={draft.objective} onChange={(e) => setDraft({ ...draft, objective: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <Label>Proposed priorities</Label>
                  <span className="text-xs text-[#1E2D44]/50">{draft.priorities.length} priorities</span>
                </div>
                <div className="space-y-2.5">
                  {draft.priorities.map((p, i) => (
                    <div key={i} className="rounded-xl border border-[#E4E8EF] p-3.5" data-testid={`draft-priority-${i}`}>
                      <div className="flex items-center gap-2">
                        <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-[#0B1729] text-xs font-bold text-[#FFC24D]">{i + 1}</span>
                        <span className="font-semibold text-[#0B1729]">{p.title}</span>
                        {p.business_area && <span className="ml-auto rounded-full bg-[#F4F6F9] px-2 py-0.5 text-[10px] font-medium text-[#1E2D44]/60">{p.business_area}</span>}
                      </div>
                      {p.why_it_matters && <p className="mt-2 text-xs text-[#1E2D44]/70"><span className="font-semibold text-[#1E2D44]/80">Why:</span> {p.why_it_matters}</p>}
                      {p.desired_outcome && <p className="mt-1 text-xs text-[#1E2D44]/70"><span className="font-semibold text-[#1E2D44]/80">Outcome:</span> {p.desired_outcome}</p>}
                      {p.success_measure && <p className="mt-1 text-xs text-[#1E2D44]/70"><span className="font-semibold text-[#1E2D44]/80">Measure:</span> {p.success_measure}</p>}
                    </div>
                  ))}
                </div>
              </div>
              <p className="rounded-lg bg-[#F4F6F9] p-3 text-xs text-[#1E2D44]/55">This is a starting point — you can edit or remove any priority after it's created.</p>
            </div>
          )}

          <DialogFooter>
            {draftStep === "input" ? (
              <>
                <Button variant="outline" onClick={() => setDraftOpen(false)}>Cancel</Button>
                <Button onClick={generate} disabled={uploading} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="generate-plan-btn">
                  <Icons.Sparkles size={16} className="mr-1.5" /> Generate plan
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setDraftStep("input")} disabled={creatingDraft} data-testid="draft-back-btn">
                  <Icons.ArrowLeft size={15} className="mr-1" /> Back
                </Button>
                <Button onClick={createFromDraft} disabled={!draft || drafting || creatingDraft} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="create-draft-plan-btn">
                  {creatingDraft ? <Icons.Loader2 className="animate-spin" size={16} /> : <><Icons.Check size={16} className="mr-1.5" /> Create this plan</>}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
